import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Save } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { MaquinaForm, MaquinaFormData } from './maquina-form';

const dadosIniciais: MaquinaFormData = {
  equipamento: '',
  tipo: '',
  area: '',
  local: '',
  tag: '',
  item: '',
  idItem: '',
  status: '',
  funcionario: '',
  observacao: '',
  refSap: '',
  refSismanaAntigo: '',
  refSismanaNovo: '',
  numeroAmt: '',
  numeroAprPraxis: '',
  template: '',
};

export function MaquinaCreate() {
  const navigate = useNavigate();
  const { projeto } = useProjeto();
  const { t } = useI18n();
  const [dados, setDados] = useState<MaquinaFormData>(dadosIniciais);
  const [salvando, setSalvando] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (campo: keyof MaquinaFormData, valor: string) => {
    setDados((prev) => ({ ...prev, [campo]: valor }));
    if (errors[campo]) {
      setErrors((prev) => { const next = { ...prev }; delete next[campo]; return next; });
    }
  };

  const validar = (): boolean => {
    const novosErrors: Record<string, string> = {};
    if (!dados.equipamento.trim()) novosErrors.equipamento = t('maquinas.form.erro.equipamentoObrigatorio');
    setErrors(novosErrors);
    return Object.keys(novosErrors).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar() || !projeto) return;

    setSalvando(true);
    try {
      const payload: any = { projetoId: projeto.id };
      (Object.keys(dados) as (keyof MaquinaFormData)[]).forEach((campo) => {
        const valor = dados[campo];
        if (valor !== '') payload[campo] = valor;
      });

      const response = await apiService.criarAtivo(payload);

      if (response.success && response.data) {
        navigate('/maquinas');
      } else {
        setErrors({ _geral: response.message || t('maquinas.form.erro.criar') });
      }
    } catch (error) {
      console.error('Erro ao criar máquina:', error);
      setErrors({ _geral: t('maquinas.form.erro.criarTentarNovamente') });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link
              to="/maquinas"
              className="text-schmersal-gray-medium dark:text-schmersal-gray-light hover:text-schmersal-blue dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">
              {t('maquinas.form.tituloNovo')}
            </h1>
          </div>
          <p className="ml-7 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('maquinas.form.subtituloNovo')}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/maquinas"
            className="inline-flex items-center justify-center px-4 py-2.5 border border-rg-line dark:border-schmersal-gray-dark rounded-lg
              text-sm font-semibold text-schmersal-gray-dark dark:text-white bg-white dark:bg-schmersal-gray-darker
              hover:border-schmersal-blue hover:text-schmersal-blue focus:outline-none focus:ring-2 focus:ring-schmersal-blue transition-all duration-200"
          >
            {t('comum.acao.cancelar')}
          </Link>
          <button
            onClick={handleSalvar}
            disabled={salvando}
            className="inline-flex items-center justify-center px-5 py-2.5 border-2 border-transparent rounded-lg
              shadow-md text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
              disabled:opacity-50 transition-all duration-200"
          >
            <Save className="h-4 w-4 mr-2" />
            {salvando ? t('comum.acao.salvando') : t('maquinas.form.salvarNova')}
          </button>
        </div>
      </div>

      {errors._geral && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm font-medium">
          {errors._geral}
        </div>
      )}

      <MaquinaForm dados={dados} onChange={handleChange} errors={errors} />
    </div>
  );
}
